import type { UsageCategoryTaxonomyService } from './UsageCategoryTaxonomyService';
import type { UsageTaxonomy, UsageTaxonomyNode } from './UsageCategoryTaxonomyTypes';

let taxonomyPromise: Promise<UsageTaxonomy> | null = null;

/** The usage-category taxonomy, fetched once per session and shared by every consumer. A failed load resolves empty and retries next call. */
export function loadTaxonomyOnce(service: UsageCategoryTaxonomyService): Promise<UsageTaxonomy> {
    taxonomyPromise ??= service.loadTaxonomy().catch(() => {
        taxonomyPromise = null;
        return { dimensionId: null, nodes: [] };
    });
    return taxonomyPromise;
}

/**
 * Maps every taxonomy node's label (lower-cased) to its top-level root's label
 * — the "usage category" a classifier value like `task_category` belongs to.
 * Walks the tree to any depth; roots map to their own label; a label appearing
 * in multiple branches keeps its first match.
 */
export function rootAncestorByLabel(nodes: UsageTaxonomyNode[]): Map<string, string> {
    const byLabel = new Map<string, string>();
    const walk = (node: UsageTaxonomyNode, rootLabel: string): void => {
        const key = node.label.trim().toLowerCase();
        if (key.length > 0 && !byLabel.has(key)) byLabel.set(key, rootLabel);
        for (const child of node.children) walk(child, rootLabel);
    };
    for (const root of nodes) walk(root, root.label);
    return byLabel;
}

/** The usage-category (root ancestor) label for a classifier value, or null when the value isn't in the taxonomy. */
export function usageCategoryFor(lookup: Map<string, string>, value: string): string | null {
    return lookup.get(value.trim().toLowerCase()) ?? null;
}
